import { communityPlanAddonApi } from "@circle-react/api/communityPlanAddonApi";
import { planSettingsPath } from "@circle-react/helpers/urlHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers/internalApi";
import { usePlanStatus } from "@circle-react/hooks/usePlanStatus";
import { useMutation, useQuery } from "react-query";

import { t } from "@/i18n-js/instance";
import type { ApiError } from "@/react/config/CustomErrors";
import type { PlanAddOn } from "@/react/types/PlanAddOn";

export const useCommunityPlanAddon = (
  addonSlug: string,
  onPurchaseError: (error: unknown) => void,
  onPurchaseSuccess: () => void,
  { enabled }: { enabled?: boolean } = { enabled: true },
) => {
  const { isCommunityOnTrial } = usePlanStatus();
  const {
    data: addOnData,
    isInitialLoading,
    refetch,
    isFetching,
  } = useQuery<PlanAddOn, ApiError>({
    queryKey: [
      internalApi.communityPlanAddons.show({
        addonId: addonSlug,
      }),
    ],
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: (_, error: ApiError) => ![401, 404].includes(error?.status),
  });

  const {
    id,
    max_purchasable_quantity: maxPurchasableQuantity,
    name,
    quantity: currentQuantity,
    unit_size: unitSize,
  } = addOnData || {};

  const canPurchase =
    !maxPurchasableQuantity ||
    (typeof currentQuantity === "number" &&
      currentQuantity < maxPurchasableQuantity);

  const canPurchaseQuantity = (quantity: number) =>
    !maxPurchasableQuantity ||
    (typeof currentQuantity === "number" &&
      currentQuantity + quantity <= maxPurchasableQuantity);

  const purchaseRequest = (quantity = 1) => {
    if (isCommunityOnTrial) {
      // TODO: use useRedirect hook once all communities have reactified settings
      return Promise.resolve().then(
        () => (window.location.href = planSettingsPath()),
      );
    }
    if (!id || !currentQuantity || !canPurchaseQuantity(quantity)) {
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- Updating typescript-eslint, should be fixed when appropriate
      return Promise.reject(
        t("addons.community_plan_addon_hook.quantity_not_available_message"),
      );
    }

    return communityPlanAddonApi.create({
      [id]:
        typeof currentQuantity === "number"
          ? currentQuantity + quantity
          : currentQuantity,
    });
  };

  const {
    mutate: purchase,
    mutateAsync: purchaseAsync,
    isPending: isPurchasing,
  } = useMutation({
    mutationFn: (quantity: number) => purchaseRequest(quantity),
    onSuccess: () => {
      void refetch();
      onPurchaseSuccess?.();
    },
    onError: err => {
      onPurchaseError?.(err);
    },
  });

  return {
    id,
    currentQuantity,
    maxPurchasableQuantity,
    name,
    unitSize,
    canPurchase,
    canPurchaseQuantity,
    purchase,
    purchaseAsync,
    refetch,
    isLoading: isInitialLoading,
    isFetching,
    isPurchasing,
  };
};
